import { useState, useEffect } from "react";
import Confetti from "react-confetti";
import { QRCodeSVG } from "qrcode.react";
import Button from "../../components/ui/button/Button.tsx";
import { useParams } from "react-router";
import api from "../../services/api";
import io from "socket.io-client";
import { AsaasFinancialNotice } from "../../components/common/AsaasSeal";

// Interfaces para tipagem dos dados da API
interface PixData {
  pixCopiaECola: string;
  [key: string]: any;
}

interface OrderItem {
  id: string;
  orderItemId: string;
  orderId: string;
  productId?: string;
  type?: string;
  unitPrice: number;
  totalPrice: number;
  quantity?: number;
  discount?: number;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  settingsData?: any;
  product?: Product;
}

interface Product {
  id: string;
  productId: string;
  name: string;
  description: string;
  imageUrl: string;
  price: number;
  discount: number;
  stock: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  settingsData: any;
  supportInfo: SupportInfo;
}

interface SupportInfo {
  id: string;
  supportInfoId: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  createdAt: string;
  updatedAt: string;
}

interface Customer {
  id: string;
  customerId: string;
  name: string;
  email: string;
  phone?: string;
  document?: string;
  documentType?: string;
  [key: string]: any;
}

interface Company {
  id: string;
  companyId: string;
  name: string;
  fantasyName: string;
  email: string;
  phone?: string;
  document?: string;
  documentType?: string;
  [key: string]: any;
}

interface OrderData {
  id: string;
  code: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  totalAmount: number;
  discountAmount: number;
  method: string;
  gateway: string;
  pixData: PixData;
  pixCopiaECola: string;
}

interface PixResponse {
  status: string;
  data: {
    order: OrderData;
    items: OrderItem[];
    customer: Customer;
    company: Company;
    payments: any[];
  };
}

const FinalizationPix = () => {
  const { id } = useParams<{ id: string }>();
  const [showConfetti, setShowConfetti] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [orderData, setOrderData] = useState<PixResponse | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  // Função para buscar os dados do pagamento PIX seguindo o padrão do projeto
  const fetchPixData = async () => {
    try {
      setLoading(true);

      // Verificar se temos o id da URL
      if (!id) {
        setError("ID do pedido não encontrado.");
        setLoading(false);
        return;
      }

      // Preparar o URL
      const url = `/api/payments/pending-pix/${id}`;

      // Chamada para a API
      const response = await api.get(url);

      // Verificar se a resposta tem o formato esperado
      if (response.data && response.data.status === 'success') {
        setOrderData(response.data);
      } else {
        console.error("Erro ao carregar dados do PIX:", response.data);
        setError("Não foi possível carregar os dados do pagamento PIX.");
      }
    } catch (err) {
      console.error("Erro ao buscar dados do PIX:", err);
      setError("Não foi possível carregar os dados do pagamento PIX. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Efeito para chamar a função de busca quando o componente montar
  useEffect(() => {
    fetchPixData();
  }, [id]);

  useEffect(() => {
    if (!orderData?.data.order.id) return;

    const roomId = orderData.data.order.id;
    console.log(`[SOCKET.IO] Conectando ao socket e entrando na sala ${roomId}`);

    // Defina a URL do socket, pode usar .env para ambiente prod
    const socket = io(import.meta.env.VITE_API_URL || "http://localhost:3000", {
      transports: ["websocket"],
      reconnection: true,
    });

    // Entra na sala específica do pedido
    socket.emit("join", { room: roomId });
    console.log(`[SOCKET.IO] Emitido evento JOIN para a sala ${roomId}.`);

    socket.on("connect", () => {
      console.log(`[SOCKET.IO] Conectado! ID do socket: ${socket.id}. Aguardando evento de Pix pago na sala ${roomId}...`);
    });

    // Escuta o evento de confirmação de pagamento Pix
    socket.on("pix_paid", (data: { redirectUrl: string }) => {
      console.log(`[SOCKET.IO] Recebido evento pix_paid para a sala ${roomId}:`, data);
      if (data?.redirectUrl) {
        window.location.href = data.redirectUrl;
      }
    });

    socket.on("disconnect", () => {
      console.log(`[SOCKET.IO] Desconectado do socket (sala ${roomId})`);
    });

    return () => {
      socket.disconnect();
      console.log(`[SOCKET.IO] Socket desconectado (sala ${roomId})`);
    };
  }, [orderData?.data.order.id]);


  // Efeito para confetti
  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Função para copiar o código PIX
  const copyPixCode = () => {
    if (orderData?.data.order.pixCopiaECola) {
      navigator.clipboard.writeText(orderData.data.order.pixCopiaECola)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        })
        .catch((err) => {
          console.error("Erro ao copiar código PIX:", err);
        });
    }
  };

  const LoadingDots = () => {
    const [dots, setDots] = useState("");

    useEffect(() => {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
      }, 600);

      return () => clearInterval(interval);
    }, []);

    return <span>{dots}</span>;
  };

  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutos em segundos

  // Timer para expiração do PIX
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Quando o tempo expirar, poderiamos redirecionar ou mostrar uma mensagem
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const minutes = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const seconds = (timeLeft % 60).toString().padStart(2, '0');
  const timeLeftDisplay = `${minutes}:${seconds}`;

  // Se estiver carregando, mostrar spinner
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
            <span className="sr-only">Carregando...</span>
          </div>
          <p>Carregando dados do pagamento PIX...</p>
        </div>
      </div>
    );
  }

  // Se houver erro, mostrar mensagem
  if (error || !orderData) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-6 bg-white shadow-lg rounded-lg">
          <div className="text-red-500 text-8xl mb-4">✗</div>
          <h2 className="text-xl font-semibold mb-4">Erro ao carregar pagamento</h2>
          <p className="mb-6">{error || "Dados do pagamento não encontrados."}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={200}
        />
      )}
      <div className="max-w-7xl mx-auto py-3 pr-3 pl-3">
        <div className="w-full md:w-3/5 mx-auto bg-white shadow-lg rounded-lg p-6 mt-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-green-600 text-xl">✔</span>
              <h1 className="text-lg font-semibold text-green-600">Pix gerado com sucesso</h1>
            </div>
            <div className="bg-gray-100 text-gray-800 font-mono text-sm py-1 px-3 rounded">
              {timeLeftDisplay}
            </div>
          </div>

          {/* Mensagem de sucesso */}
          <div className="space-y-2 mb-6 text-center">
            <h2 className="text-lg font-semibold">Seu pedido está reservado!</h2>
            <p className="text-sm">
              Finalize o pagamento via Pix em até 15 minutos para confirmar sua compra e ter acesso ao produto.
            </p>
          </div>
          <div className="w-full p-4 rounded-lg bg-yellow-100 text-yellow-800 text-center">
            <p className="text-sm font-medium">Aguardando pagamento...<LoadingDots /></p>
          </div>
          <div className="flex flex-col items-center mt-6 gap-6">
            <div className="bg-green-50 border border-green-400 rounded-lg p-2 flex justify-center">
              <QRCodeSVG
                value={orderData.data.order.pixCopiaECola || ""}
                size={250}
                level="H"
                includeMargin={false}
              />
            </div>

            <p className="text-xs text-center">Escaneie o QRCode para pagar com seu aplicativo bancário ou copie o código abaixo para pagar via "Pix copia e cola"</p>

            <div className="space-y-6 w-full">
              <div className="p-4 border-2 border-dashed border-green-500 text-center break-words">
                <p className="text-xs">{orderData.data.order.pixCopiaECola}</p>
              </div>
              <Button
                size="lg"
                className={`w-full ${copied ? 'bg-green-600' : 'bg-green-700'} text-white shadow-theme-xs hover:bg-green-800 disabled:bg-green-700`}
                onClick={copyPixCode}
              >
                {copied ? "Código copiado!" : "Clique aqui para copiar o código PIX"}
              </Button>
            </div>

            {/* Selo BaaS Asaas */}
            <AsaasFinancialNotice variant="positivo" />
          </div>
        </div>

        <div className="w-full md:w-3/5 mx-auto bg-white shadow-lg rounded-lg p-6 mt-6">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Informações do pedido */}
            <div className="flex-1">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Informações do pedido</h3>
                <div className="space-y-4">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Data do pedido:</span> {new Date(orderData.data.order.createdAt).toLocaleString('pt-BR')}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Número do pedido:</span> {orderData.data.order.id.substring(0, 8).toUpperCase()}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Código do pedido:</span> {orderData.data.order.code.toUpperCase()}
                  </p>
                  {orderData.data.payments && orderData.data.payments[0] && (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Código da transação:</span> {orderData.data.payments[0].id.substring(0, 8)}
                    </p>
                  )}
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Forma de Pagamento:</span> PIX
                  </p>
                </div>
              </div>
            </div>

            {/* Resumo do Pedido */}
            <div className="flex-1">
              <div className="space-y-6">
                <h3 className="text-lg font-semibold">Resumo do Pedido</h3>
                <div>
                  {orderData.data.items.map((item, index) => (
                    <div key={item.id || index} className="flex items-center my-4">
                      {/* Placeholder para imagem do produto */}
                      <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center mr-4">
                        <img src={item.product?.imageUrl} alt={item.product?.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1">
                        {/* Aqui poderia mostrar o nome do produto, se disponível */}
                        <p className="text-sm">{item.product?.name}</p>
                      </div>
                      <p className="text-sm">R$ {Number(item.unitPrice).toFixed(2)}</p>
                    </div>
                  ))}

                  <hr className="my-4" />

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <p className="text-base font-semibold">Total</p>
                      <p className="text-base font-semibold">
                        R$ {Number(orderData.data.order.totalAmount).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full md:w-3/5 mx-auto bg-white shadow-lg rounded-lg p-6 mt-6 justify-between">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Ajuda */}
            <div className="flex-1">
              <div className="space-y-2">
                <h3 className="text-sm">Precisa de ajuda?</h3>
                <p className="text-xs text-gray-600">Entre em contato com o produtor.</p>
              </div>
            </div>

            {/* Contato do Produtor */}
            <div className="flex-1">
              <div className="space-y-2">
                <p className="text-xs text-gray-600">
                  <span className="font-medium">Nome:</span> {orderData.data.company?.fantasyName}
                </p>
                <p className="text-xs text-gray-600">
                  <span className="font-medium">E-mail:</span> {orderData.data.items[0]?.product?.supportInfo?.email}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FinalizationPix;